'use client';

import { cn } from '@insta-club/utilities';
import { cva, type VariantProps } from 'class-variance-authority';
import Image from 'next/image';
import { FC, useEffect, useState } from 'react';
import { useFormContext } from 'react-hook-form';
import LoadingIcon from 'src/libs/components/icons/loading';

interface Option {
    value: string | number;
    label: string;
    image?: string;
    description?: string;
}

const containerVariants = cva('flex w-full flex-col gap-3', {
    variants: {
        variant: {
            default: '',
            error: ''
        }
    },
    defaultVariants: {
        variant: 'default'
    }
});

const labelVariants = cva('text-sm font-medium', {
    variants: {
        variant: {
            default: 'text-gray-700',
            error: 'text-danger-600'
        }
    },
    defaultVariants: {
        variant: 'default'
    }
});

const badgeVariants = cva(
    'inline-flex items-center gap-1.5 rounded-full border-2 px-3 py-1.5 text-sm font-medium transition-all duration-200 cursor-pointer hover:shadow-sm',
    {
        variants: {
            selected: {
                true: 'bg-primary-600 text-white border-primary-700',
                false: 'bg-primary-100 text-primary-700 border-transparent hover:bg-primary-200'
            },
            disabled: {
                true: 'cursor-not-allowed opacity-60',
                false: ''
            }
        },
        defaultVariants: {
            selected: false,
            disabled: false
        }
    }
);

export interface BadgeSelectProps extends VariantProps<typeof containerVariants> {
    name: string;
    label?: string;
    options: Option[];
    disabled?: boolean;
    multiSelect?: boolean;
    maxLength?: number;
    isLoading?: boolean;
    onChange?: (selected: Option | Option[] | null) => void;
}

const BadgeSelect: FC<BadgeSelectProps> = ({
    name,
    label,
    options,
    disabled = false,
    multiSelect = false,
    maxLength = 20,
    isLoading = false,
    variant,
    onChange
}) => {
    const { register, setValue, watch, formState } = useFormContext();
    const [hoveredBadge, setHoveredBadge] = useState<string | number | null>(null);

    const fieldValue = watch(name);
    const hasError = formState.errors[name];
    const currentVariant = hasError ? 'error' : variant;

    useEffect(() => {
        // Initialize with empty array for multi-select, null for single
        if (fieldValue === undefined) {
            setValue(name, multiSelect ? [] : null);
        }
    }, [name, multiSelect, setValue, fieldValue]);

    const isSelected = (optionValue: string | number): boolean => {
        if (multiSelect) {
            return Array.isArray(fieldValue) && fieldValue.includes(optionValue);
        }
        return fieldValue === optionValue;
    };

    const handleToggle = (option: Option) => {
        if (disabled || isLoading) return;

        if (multiSelect) {
            const currentValues = Array.isArray(fieldValue) ? fieldValue : [];
            const isCurrentlySelected = currentValues.includes(option.value);

            const newValues = isCurrentlySelected
                ? currentValues.filter((v) => v !== option.value)
                : [...currentValues, option.value];

            setValue(name, newValues);

            const selectedOptions = options.filter((opt) => newValues.includes(opt.value));
            onChange?.(selectedOptions);
        } else {
            const newValue = fieldValue === option.value ? null : option.value;
            setValue(name, newValue);

            const selectedOption = newValue ? option : null;
            onChange?.(selectedOption);
        }
    };

    const truncateText = (text: string, max: number): string => {
        if (text.length <= max) return text;
        return text.slice(0, max) + '...';
    };

    const isTruncated = (text: string, max: number): boolean => {
        return text.length > max;
    };

    return (
        <div className={cn(containerVariants({ variant: currentVariant }))}>
            {label && <label className={cn(labelVariants({ variant: currentVariant }))}>{label}</label>}

            <div className='flex flex-wrap gap-2'>
                {isLoading ? (
                    <div className='flex w-full items-center justify-center py-4'>
                        <LoadingIcon className='text-primary-600 h-6 w-6 animate-spin' />
                    </div>
                ) : (
                    options.map((option) => {
                        const selected = isSelected(option.value);
                        const showTooltip = isTruncated(option.label, maxLength);

                        return (
                            <div
                                key={option.value}
                                className='relative'
                                onMouseEnter={() => showTooltip && setHoveredBadge(option.value)}
                                onMouseLeave={() => setHoveredBadge(null)}
                            >
                                <div
                                    onClick={() => handleToggle(option)}
                                    className={cn(badgeVariants({ selected, disabled }))}
                                >
                                    {option.image && (
                                        <Image
                                            src={option.image}
                                            alt={option.label}
                                            width={16}
                                            height={16}
                                            className='rounded-sm'
                                        />
                                    )}
                                    <span>{truncateText(option.label, maxLength)}</span>
                                </div>

                                {showTooltip && hoveredBadge === option.value && (
                                    <div className='pointer-events-none absolute bottom-full left-1/2 z-50 mb-2 -translate-x-1/2 rounded-md bg-gray-900 px-3 py-1.5 text-xs whitespace-nowrap text-white shadow-lg'>
                                        {option.label}
                                        <div className='absolute top-full left-1/2 -mt-px -translate-x-1/2 border-4 border-transparent border-t-gray-900' />
                                    </div>
                                )}
                            </div>
                        );
                    })
                )}
            </div>

            {hasError && (
                <p className='text-danger-600 text-right text-sm font-medium'>
                    {formState.errors[name]?.message as string}
                </p>
            )}

            <input type='hidden' {...register(name)} />
        </div>
    );
};

export default BadgeSelect;
